import { useCallback, useEffect, useMemo, useState } from "react";
import {
  findWordMatchesInSegment,
  type Transcription,
  type TranscriptionLine,
  type WordMatchRange,
} from "../lib/transcription";

export interface FlatMatch {
  segmentIndex: number;
  startWord: number;
  endWord: number;
}

export interface SegmentMatchInfo {
  ranges: WordMatchRange[];
  /** Set of word indices that fall inside any match — for O(1) per-word lookup during render. */
  matchedWords: Set<number>;
}

export interface UseTranscriptSearchResult {
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  /** Whether the user has a non-empty query active. */
  isSearching: boolean;
  /** Per-segment match info, aligned to the transcription array. */
  segmentMatches: SegmentMatchInfo[] | undefined;
  /** Flat list of all matches across segments, in document order. */
  allMatches: FlatMatch[];
  /** Index into `allMatches` of the currently-focused match. */
  currentMatchIndex: number;
  /** The currently-focused match, or null if no matches. */
  currentMatch: FlatMatch | null;
  /** The (segmentIndex, lineIndex) containing the currently-focused match. */
  currentMatchLocation: { segmentIndex: number; lineIndex: number } | null;
  goNextMatch: () => void;
  goPrevMatch: () => void;
}

/**
 * Owns transcript search state: query, per-segment match computation,
 * flattened navigable match list, and the current-match index with
 * prev/next handlers. Renders nothing — consumers wire the returned
 * pieces into their own input and key handlers.
 *
 * Matching strategy: builds the joined word text per segment and does a
 * case-insensitive substring scan. Multi-word queries work across word
 * boundaries since the joined text includes spaces between words.
 */
export function useTranscriptSearch(
  transcription: Transcription | null,
  segmentLines: TranscriptionLine[][],
): UseTranscriptSearchResult {
  const [searchQuery, setSearchQuery] = useState("");
  const trimmedQuery = searchQuery.trim();
  const isSearching = trimmedQuery.length > 0;

  const segmentMatches = useMemo<SegmentMatchInfo[] | undefined>(() => {
    if (!transcription) return undefined;
    if (!trimmedQuery) {
      return transcription.map(() => ({
        ranges: [] as WordMatchRange[],
        matchedWords: new Set<number>(),
      }));
    }
    return transcription.map((segment) => {
      const ranges = findWordMatchesInSegment(segment.words, trimmedQuery);
      const matchedWords = new Set<number>();
      for (const r of ranges) {
        for (let i = r.startWord; i <= r.endWord; i++) matchedWords.add(i);
      }
      return { ranges, matchedWords };
    });
  }, [transcription, trimmedQuery]);

  const allMatches: FlatMatch[] = useMemo(() => {
    if (!segmentMatches) return [];
    const out: FlatMatch[] = [];
    segmentMatches.forEach((info, segmentIndex) => {
      info.ranges.forEach((r) =>
        out.push({
          segmentIndex,
          startWord: r.startWord,
          endWord: r.endWord,
        }),
      );
    });
    return out;
  }, [segmentMatches]);

  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  // Reset to first match whenever the query (or match set) changes, and
  // clamp if matches shrink under the current index.
  useEffect(() => {
    setCurrentMatchIndex((i) =>
      allMatches.length === 0 ? 0 : Math.min(i, allMatches.length - 1),
    );
  }, [trimmedQuery, allMatches.length]);

  const currentMatch =
    allMatches.length > 0 ? allMatches[currentMatchIndex] : null;

  const currentMatchLocation = useMemo(() => {
    if (!currentMatch) return null;
    const lines = segmentLines[currentMatch.segmentIndex];
    if (!lines) return null;
    const lineIdx = lines.findIndex(
      (line) =>
        currentMatch.startWord >= line.startWordIndex &&
        currentMatch.startWord < line.startWordIndex + line.words.length,
    );
    return lineIdx >= 0
      ? { segmentIndex: currentMatch.segmentIndex, lineIndex: lineIdx }
      : null;
  }, [currentMatch, segmentLines]);

  const goNextMatch = useCallback(() => {
    if (allMatches.length === 0) return;
    setCurrentMatchIndex((i) => (i + 1) % allMatches.length);
  }, [allMatches.length]);
  const goPrevMatch = useCallback(() => {
    if (allMatches.length === 0) return;
    setCurrentMatchIndex(
      (i) => (i - 1 + allMatches.length) % allMatches.length,
    );
  }, [allMatches.length]);

  return {
    searchQuery,
    setSearchQuery,
    isSearching,
    segmentMatches,
    allMatches,
    currentMatchIndex,
    currentMatch,
    currentMatchLocation,
    goNextMatch,
    goPrevMatch,
  };
}
